/* widget/src/styles/widget.module.css
 *
 * Ported (near-verbatim) from docs/widget-concept-v2.html. Demo-only
 * artifacts (client switcher, mock site sections, "Powered by Vocero"
 * credit, body[data-client] dark-mode overrides) have been removed.
 * Font vars renamed: --serif/--sans/--mono → --vw-font-display /
 * --vw-font-sans / --vw-font-mono.
 *
 * CSS-Modules note: every classname here is hashed at build time. We
 * keep the class hierarchy from the concept HTML so the compound
 * selectors (e.g. ".widget-host.active .messages") still match — both
 * classnames in the compound resolve to their hashed forms but the
 * structural relationship is preserved.
 */

/* The fractal-noise `::before` overlay from the original Vocero concept
 * was removed — its `mix-blend-mode: multiply` produced a gritty
 * textured wash that stacked on top of the scrim and read as "weird
 * overlay" rather than a clean dim. The scrim below now does all the
 * background-darkening work on its own. */

/* ============ THE WIDGET ============ */
.widget-host {
  position: fixed;
  inset: 0;
  z-index: 50;
  pointer-events: none;
}
.widget-host > * {
  pointer-events: auto;
}

/* Page dim when active.
 *
 * Strong modal-scrim — the chat is foreground, the page goes away.
 * 72% ink + 16px blur means the storefront behind reads as a flat
 * dark ground regardless of whether it was white, full-bleed photo,
 * branded color, or a busy collection grid. The chat content sits on
 * what's effectively a clean canvas. */
.scrim {
  position: fixed;
  inset: 0;
  background: rgba(14, 13, 11, 0);
  backdrop-filter: blur(0);
  -webkit-backdrop-filter: blur(0);
  transition: background var(--vw-dur-slow) var(--vw-ease),
    backdrop-filter var(--vw-dur-slow) var(--vw-ease);
  pointer-events: none;
}
.widget-host.active .scrim {
  /* Mobile-only in practice (desktop hides the scrim). Pushed to 0.88 ink
   * + 24px blur so the storefront recedes hard and focus moves into the
   * chat — the lighter 0.72 left the page too legible/distracting. */
  background: rgba(14, 13, 11, 0.88);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  pointer-events: auto;
}

/* The orb — bottom-right anchor, no panel */
.orb-stack {
  position: absolute;
  bottom: max(20px, env(safe-area-inset-bottom));
  right: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  width: min(480px, calc(100vw - 40px));
}
@media (min-width: 768px) {
  .orb-stack {
    bottom: 28px;
    right: 28px;
    gap: 14px;
    width: 520px;
  }

  /* Desktop: drop the page-wide scrim. The customer is still browsing
   * dipyourcar.com; dimming the whole storefront for a chat overlay
   * was overreach. Close stays available via the X (top-right of the
   * stack) and ESC. Mobile keeps the page-wide scrim because the
   * mobile sheet treatment depends on it. */
  .scrim {
    display: none;
  }

  /* No desktop backdrop. Bubbles use the MMY-exact recipe (20% white
   * glass + blur + shadow) and stand on whatever the storefront page
   * provides behind them. On photo / hero / branded sections the
   * backdrop-filter does real glass work; on flat-white sections the
   * cards are intentionally subtle. */
}

/* The .hint pill was removed in favor of a bare-orb CTA on desktop.
 * The class is no longer rendered by ChatWidget.tsx; any remaining
 * stale style references are no-ops. */

/* Floating message cards stack above orb */
.messages {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  align-items: stretch;
  max-height: 0;
  overflow: hidden;
  transition: max-height var(--vw-dur-slow) var(--vw-ease);
}
.widget-host.active .messages {
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: visible;
  padding: 4px;
  scrollbar-width: thin;
  scrollbar-color: var(--vw-rule) transparent;
}
.messages::-webkit-scrollbar {
  width: 4px;
}
.messages::-webkit-scrollbar-thumb {
  background: var(--vw-rule);
  border-radius: 2px;
}

.msg {
  animation: msg-in var(--vw-dur-slow) var(--vw-ease) backwards;
  position: relative;
}
@keyframes msg-in {
  0% {
    opacity: 0;
    transform: translateY(14px) scale(0.96);
  }
  60% {
    opacity: 1;
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Glass bubble system (Figma-inspired).
 *
 * Both assistant and user use the same material: a 10%-white glass tint
 * with real backdrop blur, on the widget's deep cool-near-black
 * substrate. Speaker is communicated by alignment (assistant left,
 * user right) and by the gradient Pearl avatar; the bubble shape itself
 * is symmetric. 24px radius — closer to a speech pill than the previous
 * 14px byline-card. Real backdrop-filter is justified here: cards sit
 * on a deliberate dark substrate the widget supplies, so the blur isn't
 * decorative — it's the material itself. */

.msg.assistant,
.msg.user {
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.msg.assistant {
  align-self: flex-start;
  max-width: 92%;
}
.msg.user {
  align-self: flex-end;
  max-width: 88%;
  flex-direction: row; /* avatar trails the card on the right */
}

/* Avatar slot — 40px circle next to every message. Pearl gets the
 * iridescent gradient orb (the widget's signature mark); the customer
 * gets a quieter glass disk. Both sit at the bubble's bottom edge,
 * Figma-style. */
.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  position: relative;
}
.avatar-pearl {
  /* DYC brand mark — the official avatar PNG at
   * widget/src/assets/dyc-avatar.png. Bundled by Vite at build time. */
  background: url("../assets/dyc-avatar.png") center / 100% 100% no-repeat;
}
.avatar-user {
  background: var(--vw-card-bg-strong);
  backdrop-filter: blur(16px) saturate(140%);
  -webkit-backdrop-filter: blur(16px) saturate(140%);
  border: 1px solid var(--vw-card-border-strong);
}

.msg.assistant .card,
.msg.user .card {
  /* Self-contained MMY glass. The bubble carries its own dark base
   * (bottom layer) + the 20% white-glass tint (top layer), so the
   * glass effect is visible regardless of the storefront page behind.
   * Dark base at 0.92 (was 0.85) — less see-through so the bubble reads
   * as a solid surface, with a two-layer shadow for lift. */
  background:
    linear-gradient(rgba(255, 255, 255, 0.20), rgba(255, 255, 255, 0.20)),
    rgba(14, 13, 11, 0.92);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.30);
  border-radius: 24px;
  padding: 12px 16px;
  color: var(--vw-text-bright);
  /* min-width:0 lets the card shrink inside the flex row (avatar + card)
   * so long content wraps instead of overflowing the viewport. */
  min-width: 0;
  box-shadow:
    0 2px 6px rgba(0, 0, 0, 0.22),
    0 12px 28px -6px rgba(0, 0, 0, 0.34);
}

/* Tail dot — the small floating glass dot near the bubble's bottom
 * outside corner. Same stacked recipe as the bubble itself so it
 * reads as the same material. */
.msg.assistant .card::before,
.msg.user .card::before {
  content: "";
  position: absolute;
  bottom: 2px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background:
    linear-gradient(rgba(255, 255, 255, 0.20), rgba(255, 255, 255, 0.20)),
    rgba(14, 13, 11, 0.92);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.30);
}
.msg.assistant .card::before { left: -10px; }
.msg.user .card::before { right: -10px; }

.msg.assistant .meta {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
}
.msg.assistant .meta-name {
  font-family: var(--vw-font-sans);
  font-weight: 600;
  font-size: 14px;
  color: var(--vw-text-bright);
  letter-spacing: var(--vw-track-display);
  line-height: 1;
}
.msg.assistant .meta-time {
  font-family: var(--vw-font-mono);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--vw-text-bright-meta);
  line-height: 1;
}

/* Beta indicator — small mono pill next to Pearl's name. Subtle but
 * always present so customers know Pearl is in beta. */
.beta-tag {
  font-family: var(--vw-font-mono);
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--vw-text-bright-meta);
  border: 1px solid var(--vw-card-border-strong);
  border-radius: 4px;
  padding: 1px 4px;
  margin-left: 6px;
  vertical-align: middle;
  line-height: 1.4;
}
.msg.assistant .text,
.msg.user .text {
  font-family: var(--vw-font-sans);
  font-weight: 400;
  font-size: var(--vw-text-body);
  line-height: var(--vw-leading-body);
  letter-spacing: var(--vw-track-display);
  white-space: pre-wrap;
  /* Break long unbreakable strings (raw URLs, tracking numbers) so they
   * wrap inside the bubble instead of forcing it wider than the
   * viewport. */
  overflow-wrap: anywhere;
  word-break: break-word;
  color: var(--vw-text-bright);
}
.msg.assistant .text em {
  font-style: italic;
  font-weight: 500;
  color: var(--vw-accent);
}
.msg.assistant .text strong,
.msg.user .text strong {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.98);
}

/* Pending caret while assistant is streaming */
.caret {
  display: inline-block;
  width: 0.5ch;
  margin-left: 1px;
  opacity: 0.8;
  animation: caret-blink 0.9s steps(2, start) infinite;
}
@keyframes caret-blink {
  to {
    opacity: 0;
  }
}

/* Tool indicator inline under assistant text */
.tool-line {
  margin-top: 6px;
  font-family: var(--vw-font-mono);
  font-size: var(--vw-text-meta);
  text-transform: uppercase;
  letter-spacing: var(--vw-track-mono);
  color: var(--vw-text-bright-soft);
  font-style: normal;
}

/* Quick reply chips */
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-self: flex-start;
  max-width: 100%;
  animation: msg-in var(--vw-dur-slow) var(--vw-ease) 0.08s backwards;
  margin-top: 4px;
}
.chip {
  background:
    linear-gradient(rgba(255, 255, 255, 0.20), rgba(255, 255, 255, 0.20)),
    rgba(14, 13, 11, 0.92);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.30);
  color: var(--vw-text-bright);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 8px 14px;
  font-family: var(--vw-font-sans);
  font-size: var(--vw-text-chip);
  font-weight: 500;
  cursor: pointer;
  border-radius: var(--vw-radius-chip);
  transition: background var(--vw-dur-snap) var(--vw-ease),
    border-color var(--vw-dur-snap) var(--vw-ease),
    color var(--vw-dur-snap) var(--vw-ease),
    transform var(--vw-dur-snap) var(--vw-ease);
  letter-spacing: var(--vw-track-display);
  white-space: nowrap;
}
.chip:hover {
  /* Subtle alteration only — same dark base, slightly brighter white
   * tint and edge. Stays in the same glass family rather than flipping
   * to accent red on hover (the accent is reserved for the send button
   * and other commit-actions). */
  background:
    linear-gradient(rgba(255, 255, 255, 0.30), rgba(255, 255, 255, 0.30)),
    rgba(14, 13, 11, 0.92);
  border-color: rgba(255, 255, 255, 0.40);
  color: var(--vw-text-bright);
  transform: translateY(-1px);
}
.chip:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--vw-accent-ring), var(--vw-shadow-soft);
}
.chip:active {
  transform: translateY(0) scale(0.98);
}

/* Composing indicator — same byline-card shape as the assistant
 * message, just narrower content (label + dots). Sized to match a
 * one-line bubble so it slots into the conversation without looking
 * like a separate component. */
.composing {
  display: none;
  align-self: flex-start;
  align-items: center;
  gap: 8px;
  background:
    linear-gradient(rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0.18)),
    rgba(14, 13, 11, 0.92);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.28);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 10px 14px;
  border-radius: 24px;
  font-family: var(--vw-font-sans);
  font-size: var(--vw-text-chip);
  font-weight: 500;
  letter-spacing: var(--vw-track-display);
  color: var(--vw-text-bright-soft);
  animation: msg-in var(--vw-dur-base) var(--vw-ease) backwards;
}
.widget-host.thinking .composing {
  display: inline-flex;
}
.composing-dots {
  display: inline-flex;
  gap: 3px;
  margin-left: 4px;
}
.composing-dots i {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  /* Pinned to the send-button blue (MMY #6DC3F7), NOT --vw-accent —
   * the dashboard widget-config can override --vw-accent at runtime
   * (which was rendering the dots dark blue). The thinking dots should
   * always match the send button. */
  background: rgba(109, 195, 247, 0.9);
  animation: dot-bounce 1.2s ease-in-out infinite;
}
.composing-dots i:nth-child(2) {
  animation-delay: 0.15s;
}
.composing-dots i:nth-child(3) {
  animation-delay: 0.3s;
}
@keyframes dot-bounce {
  0%,
  100% {
    opacity: 0.3;
    transform: translateY(0);
  }
  50% {
    opacity: 1;
    transform: translateY(-3px);
  }
}

/* Composer.
 *
 * The previous "pill with focus-glow ring" produced two visual issues
 * the customer flagged: (1) the box-shadow glow read as an internal
 * rectangle on iOS, and (2) the 999px pill clashed with the byline
 * cards' 14px radius. New shape is a soft rounded rectangle that
 * matches the card system and uses border-color (not box-shadow) to
 * signal focus, so there's no ghost-rectangle on any platform.
 *
 * iOS bonus: explicit `appearance: none` on the textarea kills the
 * mobile-Safari default field styling that was bleeding through. */
.composer-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  background:
    linear-gradient(rgba(255, 255, 255, 0.22), rgba(255, 255, 255, 0.22)),
    rgba(14, 13, 11, 0.92);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  /* Transparent border (not none) preserves the 1px sizing while
   * removing the visible stroke the customer flagged. The dark-glass
   * background delimits the pill on its own. */
  border: 1px solid transparent;
  border-radius: 22px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 6px 6px 6px 16px;
  width: 100%;
  opacity: 0;
  transform: translateY(12px);
  pointer-events: none;
  transition: opacity var(--vw-dur-base) var(--vw-ease) 80ms,
    transform var(--vw-dur-base) var(--vw-ease) 80ms;
}
.widget-host.active .composer-pill {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}
/* No focus ring on the pill container — the caret is the focus signal.
 * Belt-and-suspenders against the "black square border" some browsers
 * draw on focus-within of a flex container holding a textarea. */
.composer-pill:focus-within {
  outline: none;
}
/* No :focus-within border or glow — the caret in the textarea is the
 * focus signal. Adding a border darken or shadow ring just reads as
 * "form field that was selected", which is the visual debt the
 * customer flagged. */
.composer {
  flex: 1;
  background: transparent;
  border: 0;
  outline: 0;
  -webkit-appearance: none;
  appearance: none;
  -webkit-tap-highlight-color: transparent;
  padding: 10px 0;
  font-family: var(--vw-font-sans);
  font-size: var(--vw-text-body);
  color: #ffffff;
  caret-color: rgba(109, 195, 247, 0.9);
  resize: none;
  line-height: var(--vw-leading-body);
  max-height: 96px;
  min-width: 0;
  box-shadow: none;
}
/* Belt-and-suspenders: explicit overrides for every focus pseudo-state.
 * !important is justified here — the widget renders in the host
 * storefront's DOM (no shadow root), so a global `textarea:focus` /
 * `input:focus` outline rule in the Shopify theme can bleed in and
 * draw the "square border on click". These win regardless of host CSS. */
.composer,
.composer:focus,
.composer:focus-visible,
.composer:active {
  outline: none !important;
  border: none !important;
  box-shadow: none !important;
  -webkit-appearance: none !important;
  appearance: none !important;
}
.composer::placeholder {
  color: var(--vw-text-bright-soft);
  font-family: var(--vw-font-sans);
  font-weight: 400;
  letter-spacing: var(--vw-track-display);
}
.send-btn {
  width: 36px;
  height: 36px;
  border-radius: var(--vw-radius-pill);
  /* MMY-finder blue (#6DC3F7) — same colour as the carousel "Search"
   * button so chat and finder share one action signal. Solid fill at
   * 90% alpha with a thin matched border, matching the
   * .finder-search-btn recipe in DYC-Shopify make-model-year-finder.css. */
  background: rgba(109, 195, 247, 0.9);
  border: 1px solid rgba(109, 195, 247, 0.5);
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: background var(--vw-dur-snap) var(--vw-ease),
    border-color var(--vw-dur-snap) var(--vw-ease),
    transform var(--vw-dur-snap) var(--vw-ease),
    opacity var(--vw-dur-snap) var(--vw-ease);
}
.send-btn:hover:not(:disabled) {
  background: rgba(109, 195, 247, 1);
  border-color: rgba(109, 195, 247, 0.8);
  box-shadow: 0 6px 20px rgba(109, 195, 247, 0.3);
}
.send-btn:active:not(:disabled) {
  transform: scale(0.94);
}
.send-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--vw-accent-ring);
}
.send-btn:disabled {
  opacity: 0.25;
  cursor: not-allowed;
}
.send-btn svg {
  width: 14px;
  height: 14px;
}

/* === The Voice Orb === */
.orb-row {
  display: flex;
  align-items: center;
  gap: 10px;
  align-self: flex-end;
}

/* Live oscilloscope canvas — replaces orb visually when recording */
.scope {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--vw-accent);
  box-shadow: 0 12px 30px -10px rgba(217, 68, 44, 0.6),
    0 0 0 0 var(--vw-accent);
  overflow: hidden;
  display: none;
  align-items: center;
  justify-content: center;
  position: relative;
  animation: rec-halo 1.4s ease-in-out infinite;
}
.scope canvas {
  width: 100%;
  height: 100%;
}
@keyframes rec-halo {
  0%,
  100% {
    box-shadow: 0 12px 30px -10px var(--vw-accent),
      0 0 0 0 var(--vw-accent-soft);
  }
  50% {
    box-shadow: 0 12px 30px -10px var(--vw-accent), 0 0 0 14px transparent;
  }
}
.widget-host.recording .scope {
  display: flex;
}
.widget-host.recording .orb {
  display: none;
}

.orb {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--vw-ink);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  position: relative;
  box-shadow: var(--vw-shadow-deep);
  transition: transform var(--vw-dur-snap) var(--vw-ease),
    box-shadow var(--vw-dur-base) var(--vw-ease);
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}
.orb:hover {
  transform: scale(1.04);
  box-shadow: var(--vw-shadow-deep), 0 0 0 6px var(--vw-accent-soft);
}
.orb:active {
  transform: scale(0.96);
}
.orb:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--vw-accent-ring), var(--vw-shadow-deep);
}

/* Subtle inner highlight — gives the orb depth without ambient motion. */
.orb::before {
  content: "";
  position: absolute;
  inset: 4px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 30% 30%,
    rgba(255, 255, 255, 0.10),
    transparent 60%
  );
}

/* Halo expansion — fires on the .thinking state only. Single iteration
 * per cycle, eased out. Idle has zero ambient motion in the halo layer;
 * we don't want the orb pinging at customers when nothing's happening. */
.orb::after {
  content: "";
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 1.5px solid var(--vw-accent);
  opacity: 0;
}
.widget-host.thinking .orb::after {
  animation: orb-ring 2.4s var(--vw-ease) infinite;
}
@keyframes orb-ring {
  0% {
    opacity: 0.55;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(1.45);
  }
}

/* Waveform inside orb. Idle uses a slow, gentle breath (2.4s, narrow
 * scale range); thinking accelerates it and switches to the accent. */
.orb .bar {
  width: 3px;
  background: var(--vw-paper);
  border-radius: 2px;
  animation: orb-pulse 2.4s ease-in-out infinite;
  transform-origin: center;
}
.orb .bar:nth-child(1) {
  animation-delay: 0s;
  height: 10px;
}
.orb .bar:nth-child(2) {
  animation-delay: 0.12s;
  height: 18px;
}
.orb .bar:nth-child(3) {
  animation-delay: 0.24s;
  height: 26px;
}
.orb .bar:nth-child(4) {
  animation-delay: 0.36s;
  height: 18px;
}
.orb .bar:nth-child(5) {
  animation-delay: 0.48s;
  height: 10px;
}
@keyframes orb-pulse {
  0%,
  100% {
    transform: scaleY(0.55);
  }
  50% {
    transform: scaleY(0.95);
  }
}
.widget-host.thinking .orb .bar {
  animation-duration: 0.7s;
  background: var(--vw-accent);
}
@keyframes orb-pulse-active {
  0%,
  100% {
    transform: scaleY(0.3);
  }
  50% {
    transform: scaleY(1);
  }
}
.widget-host.thinking .orb .bar {
  animation-name: orb-pulse-active;
}

/* Mic-anchor: long-press the orb itself to record */
.orb.recording-arm {
  background: var(--vw-accent);
}

/* Close pill — sits at the top-right of the floating stack when active */
.close-pill {
  width: 32px;
  height: 32px;
  border-radius: var(--vw-radius-pill);
  background:
    linear-gradient(rgba(255, 255, 255, 0.20), rgba(255, 255, 255, 0.20)),
    rgba(14, 13, 11, 0.92);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.30);
  color: var(--vw-text-bright);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: flex-end;
  margin-bottom: 4px;
  opacity: 0;
  transform: scale(0.85) translateY(-4px);
  pointer-events: none;
  transition: opacity var(--vw-dur-base) var(--vw-ease) 160ms,
    transform var(--vw-dur-base) var(--vw-ease) 160ms,
    background var(--vw-dur-snap) var(--vw-ease),
    color var(--vw-dur-snap) var(--vw-ease),
    border-color var(--vw-dur-snap) var(--vw-ease);
}
.widget-host.active .close-pill {
  opacity: 1;
  transform: scale(1) translateY(0);
  pointer-events: auto;
}
.close-pill:hover {
  background: rgba(255, 255, 255, 0.18);
  color: var(--vw-paper);
  border-color: rgba(255, 255, 255, 0.22);
}
.close-pill:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--vw-accent-ring), var(--vw-shadow-soft);
}
.close-pill svg {
  width: 12px;
  height: 12px;
}

/* Hold-to-record hint over orb */
.rec-hint {
  position: absolute;
  bottom: calc(100% + 14px);
  right: 0;
  background: var(--vw-ink);
  color: var(--vw-paper);
  font-family: var(--vw-font-mono);
  font-size: var(--vw-text-meta);
  text-transform: uppercase;
  letter-spacing: var(--vw-track-mono);
  padding: 8px 12px;
  border-radius: var(--vw-radius-pill);
  box-shadow: var(--vw-shadow-soft);
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transform: translateY(6px);
  transition: opacity var(--vw-dur-snap) var(--vw-ease),
    transform var(--vw-dur-snap) var(--vw-ease);
}
.widget-host.recording .rec-hint {
  opacity: 1;
  transform: translateY(0);
}

/* Attachments under assistant cards — neutral container, the inner
 * cards bring their own styling. */
.attachments {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ---------------------------------------------------------------------
 * Rich attachment cards — Product, Color, Video, Tracking, Discount,
 * EmailCapture. All share the same surface treatment (paper-tinted
 * glass, hairline border, card radius). Specific layouts compose on
 * top via .rc-image, .rc-body, .rc-title, .rc-sub, etc.
 * --------------------------------------------------------------------- */
.rc {
  display: block;
  text-decoration: none;
  color: inherit;
  background: var(--vw-card-bg-soft);
  border: 1px solid var(--vw-card-border);
  border-radius: var(--vw-radius-card);
  overflow: hidden;
  position: relative;
  transition: border-color var(--vw-dur-snap) var(--vw-ease),
    transform var(--vw-dur-snap) var(--vw-ease);
}
a.rc:hover,
button.rc:hover {
  border-color: var(--vw-card-border-strong);
  transform: translateY(-1px);
}
a.rc:focus-visible,
button.rc:focus-visible {
  outline: none;
  border-color: var(--vw-accent);
  box-shadow: 0 0 0 3px var(--vw-accent-ring);
}
.rc-image {
  width: 100%;
  /* All DYC product images are 1:1 (square) at the source — render them
   * 1:1 here too so a landscape image gets center-cropped instead of
   * letterboxed (caught 2026-06-03: a 16:9 image rendered into the 88px
   * fixed-height box was producing stretched / oddly cropped tiles in
   * the canvas grid). Keeps the card a predictable square so 4-up and
   * 6-up grids stay tidy at any width. */
  aspect-ratio: 1 / 1;
  object-fit: cover;
  display: block;
  background: var(--vw-card-bg-soft);
}
.rc-body {
  padding: 7px 10px;
}
.rc-title {
  font-family: var(--vw-font-sans);
  font-weight: 600;
  font-size: var(--vw-text-chip);
  letter-spacing: var(--vw-track-display);
  color: var(--vw-ink);
  line-height: var(--vw-leading-display);
}
.rc-sub {
  font-family: var(--vw-font-sans);
  font-size: var(--vw-text-meta);
  color: var(--vw-ink-3);
  margin-top: 3px;
  line-height: 1.35;
}
.rc-price-row {
  margin-top: 4px;
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-family: var(--vw-font-sans);
}
.rc-price {
  font-weight: 600;
  font-size: var(--vw-text-chip);
  color: var(--vw-ink);
  font-variant-numeric: tabular-nums;
}
.rc-price-compare {
  font-size: var(--vw-text-meta);
  color: var(--vw-ink-3);
  text-decoration: line-through;
  font-variant-numeric: tabular-nums;
}
/* "View product →" tappability cue. The whole .rc card is an <a>, but
 * customers in the canvas grid don't always realise tiles navigate. This
 * row reads as a link affordance without adding an extra button or
 * inflating tile height materially. */
.rc-cta-row {
  margin-top: 5px;
}
.rc-cta-link {
  font-family: var(--vw-font-sans);
  font-size: var(--vw-text-meta);
  font-weight: 600;
  /* White, not the blue accent — on the dark product card the blue was
   * unreadable. Overridable so the classic light widget can re-tint if needed. */
  color: var(--vw-cta-link-color, #fff);
  letter-spacing: -0.005em;
}
/* Interactive ProductCard — variant pills, fitment line, add-to-cart button.
 * These classes only apply when the card has live variants from the agent
 * console (Task 8). The no-variant path keeps the existing <a>.rc layout. */
.rc-fitment {
  font-family: var(--vw-font-sans);
  font-size: var(--vw-text-meta);
  /* Full-opacity ink — vehicle compatibility should read clearly, no
   * transparency (per Justin 2026-06-05). */
  color: var(--vw-ink);
  margin-top: 3px;
  line-height: 1.35;
}
.rc-variants {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 7px;
  margin-bottom: 2px;
}
/* Un-selected pill — bordered, muted background */
.rc-pill {
  font-family: var(--vw-font-sans);
  font-size: var(--vw-text-meta);
  font-weight: 500;
  letter-spacing: var(--vw-track-display);
  padding: 4px 10px;
  border-radius: var(--vw-radius-chip);
  border: 1px solid var(--vw-card-border-strong);
  background: transparent;
  color: var(--vw-ink);
  cursor: pointer;
  transition: background var(--vw-dur-snap) var(--vw-ease),
    border-color var(--vw-dur-snap) var(--vw-ease),
    color var(--vw-dur-snap) var(--vw-ease);
}
.rc-pill:hover:not(:disabled) {
  background: var(--vw-card-bg-soft);
  border-color: var(--vw-accent);
}
.rc-pill:disabled {
  opacity: 0.38;
  cursor: not-allowed;
}
/* Selected pill — overrides applied ON TOP of .rc-pill (the component sets
 * both classes), so only the diffs live here. The #6DC3F7 / #0d1117 hexes
 * are pinned deliberately (not --vw-accent), matching .rc-cta-build: the
 * selected state must stay DYC brand blue even when a dashboard widget-config
 * overrides --vw-accent. (:disabled is covered by .rc-pill:disabled.) */
.rc-pill-on {
  font-weight: 600;
  border-color: #6DC3F7;
  background: #6DC3F7;
  color: #fff;
  transition: background var(--vw-dur-snap) var(--vw-ease),
    filter var(--vw-dur-snap) var(--vw-ease);
}
.rc-pill-on:hover:not(:disabled) {
  filter: brightness(1.08);
}
/* "Add to cart" — full-width CTA. Brand-blue hexes pinned (not --vw-accent),
 * same rationale as .rc-pill-on / .rc-cta-build. */
.rc-add {
  display: block;
  width: 100%;
  margin-top: 8px;
  padding: 9px 12px;
  background: #6DC3F7;
  color: #fff;
  border: none;
  border-radius: var(--vw-radius-pill);
  font-family: var(--vw-font-sans);
  font-size: var(--vw-text-chip);
  font-weight: 700;
  letter-spacing: var(--vw-track-display);
  cursor: pointer;
  transition: filter var(--vw-dur-snap) var(--vw-ease),
    transform var(--vw-dur-snap) var(--vw-ease);
}
.rc-add:hover:not(:disabled) {
  filter: brightness(1.07);
  transform: translateY(-1px);
}
.rc-add:active:not(:disabled) {
  transform: translateY(0) scale(0.97);
  filter: brightness(0.98);
}
.rc-add:disabled {
  opacity: 0.38;
  cursor: not-allowed;
}

.rc-label {
  font-family: var(--vw-font-mono);
  font-size: var(--vw-text-meta);
  text-transform: uppercase;
  letter-spacing: var(--vw-track-mono);
  color: var(--vw-ink-3);
}

/* Video card — play affordance overlaid on the thumbnail. */
.rc-video-scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(14, 13, 11, 0) 50%,
    rgba(14, 13, 11, 0.35) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.rc-video-play {
  width: 34px;
  height: 34px;
  border-radius: var(--vw-radius-pill);
  background: var(--vw-paper);
  color: var(--vw-ink);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--vw-shadow-soft);
}
.rc-video-play svg {
  width: 13px;
  height: 13px;
  margin-left: 2px;
}

/* Tracking — small CTA pill inside the body, accent-filled. */
.rc-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 7px 12px;
  /* CTA background/text are overridable so a dark surface (the Fin panel)
   * can recolor the button without disturbing the classic light widget,
   * which falls back to the original ink/paper pair. */
  background: var(--vw-cta-bg, var(--vw-ink));
  color: var(--vw-cta-fg, var(--vw-paper));
  border-radius: var(--vw-radius-pill);
  font-size: var(--vw-text-meta);
  font-weight: 500;
  letter-spacing: var(--vw-track-display);
  text-decoration: none;
  transition: background var(--vw-dur-snap) var(--vw-ease);
}
.rc-cta:hover {
  background: var(--vw-cta-bg-hover, var(--vw-accent));
}

/* Build-summary "Add to cart" — brand-blue (#6dc3f7) with white text and a
 * gentle attention pulse so the primary buy action reads as the obvious next
 * step. Hover brightens + lifts; press dips. !important so it wins over the
 * card's inline layout styles. */
@keyframes rc-cta-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(109, 195, 247, 0);
  }
  50% {
    box-shadow: 0 0 0 5px rgba(109, 195, 247, 0.26);
  }
}
.rc-cta-build {
  background: #6dc3f7 !important;
  color: #fff !important;
  transition:
    transform 140ms cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 200ms ease,
    filter 140ms ease !important;
  animation: rc-cta-pulse 2.4s ease-in-out infinite;
}
.rc-cta-build:hover {
  filter: brightness(1.07);
  transform: translateY(-1px);
}
.rc-cta-build:active {
  transform: translateY(0) scale(0.96);
  filter: brightness(0.98);
}
/* "Added ✓" success state — green fill, brief scale pulse on success. */
.rc-add-added {
  background: #4caf86 !important;
  cursor: default !important;
  transform: none;
}
/* Scale pulse on success — one-shot, not looping. Applied only when the
 * .rc-add-pulse class is present (removed when prefers-reduced-motion). */
@keyframes rc-add-check-pulse {
  0% { transform: scale(1); }
  40% { transform: scale(1.05); }
  80% { transform: scale(0.98); }
  100% { transform: scale(1); }
}
.rc-add-pulse {
  animation: rc-add-check-pulse 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
/* Inline spinner inside the add-to-cart button while the request is in flight. */
@keyframes rc-add-spin {
  to { transform: rotate(360deg); }
}
.rc-add-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  animation: rc-add-spin 0.65s linear infinite;
  vertical-align: middle;
}

@media (prefers-reduced-motion: reduce) {
  .rc-cta-build {
    animation: none;
  }
  .rc-add:hover:not(:disabled),
  .rc-add:active:not(:disabled) {
    transform: none;
  }
  .rc-add-pulse {
    animation: none;
  }
}

/* Discount — soft-accent pill with a mono code. Dashed border reads as
 * "voucher" without resorting to a coupon clipart. */
.rc-discount {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 0;
  padding: 8px 14px;
  background: var(--vw-accent-soft);
  border: 1px dashed var(--vw-accent);
  border-radius: var(--vw-radius-pill);
  font-size: var(--vw-text-chip);
  color: var(--vw-ink);
}
.rc-discount-code {
  font-family: var(--vw-font-mono);
  font-weight: 600;
  letter-spacing: var(--vw-track-mono);
  color: var(--vw-accent);
}
.rc-discount-expires {
  opacity: 0.6;
  font-size: var(--vw-text-meta);
}

/* Email capture — inline pill form. The composer pill's visual cousin. */
.rc-email-form {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--vw-card-bg);
  border: 1px solid var(--vw-card-border-strong);
  border-radius: var(--vw-radius-pill);
  padding: 4px;
  transition: border-color var(--vw-dur-snap) var(--vw-ease),
    box-shadow var(--vw-dur-snap) var(--vw-ease);
}
.rc-email-form:focus-within {
  border-color: var(--vw-accent);
  box-shadow: 0 0 0 3px var(--vw-accent-ring);
}
.rc-email-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  padding: 6px 10px;
  font-family: var(--vw-font-sans);
  /* 16px MANDATORY — sub-16px inputs trigger iOS Safari auto-zoom on focus.
     (Was var(--vw-text-chip) = 13px → every email capture zoomed the page.) */
  font-size: 16px;
  color: var(--vw-ink);
  min-width: 0;
}
.rc-email-input::placeholder {
  color: var(--vw-ink-3);
}
.rc-email-submit {
  background: var(--vw-ink);
  color: var(--vw-paper);
  border: none;
  border-radius: var(--vw-radius-pill);
  padding: 6px 14px;
  font-family: var(--vw-font-sans);
  font-size: var(--vw-text-meta);
  font-weight: 500;
  letter-spacing: var(--vw-track-display);
  cursor: pointer;
  transition: background var(--vw-dur-snap) var(--vw-ease);
}
.rc-email-submit:hover {
  background: var(--vw-accent);
}
.rc-email-submit:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--vw-accent-ring);
}

/* When 2+ colors come back from find_color, render them in a 2-column
 * grid instead of a vertical stack. ColorCard's outer element has
 * margin-top:10 baked in via inline style, so reset it here — gap
 * handles spacing inside the grid. */
.color-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}
.color-grid > * {
  margin-top: 0 !important;
}

/* When 2+ products come back, render them 2-up in a grid with square
 * images — compact product tiles instead of full-width landscape
 * banners. A single product still renders full-width (see MessageCard
 * renderAttachments). */
.product-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}
.product-grid > * {
  margin-top: 0 !important;
}
.product-grid .rc-image {
  height: auto;
  aspect-ratio: 1 / 1;
}

/* ============================================================
 * Mobile sheet header — hidden on desktop. The header carries Pearl's
 * avatar/name on the left and the close X on the right, replacing the
 * floating .close-pill (which is hidden via @media below).
 * ============================================================ */
.sheet-header {
  display: none;
}
.sheet-title {
  display: flex;
  align-items: center;
  gap: 10px;
}
.sheet-avatar {
  width: 36px;
  height: 36px;
  border-radius: var(--vw-radius-pill);
  /* Same DYC avatar PNG as the inline message avatars — consistent
   * brand across the sheet header and every Pearl turn. */
  background: url("../assets/dyc-avatar.png") center / 100% 100% no-repeat;
  color: transparent;
  font-size: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.sheet-titleblock {
  display: flex;
  flex-direction: column;
  line-height: var(--vw-leading-display);
}
.sheet-name {
  font-family: var(--vw-font-sans);
  font-weight: 600;
  font-size: var(--vw-text-body);
  color: var(--vw-text-bright);
  letter-spacing: var(--vw-track-display);
}
.sheet-sub {
  font-family: var(--vw-font-mono);
  font-size: var(--vw-text-meta);
  text-transform: uppercase;
  letter-spacing: var(--vw-track-mono);
  color: var(--vw-text-bright-meta);
  margin-top: 2px;
}
.sheet-close {
  width: 36px;
  height: 36px;
  border-radius: var(--vw-radius-pill);
  border: 1px solid var(--vw-card-border-strong);
  background: var(--vw-card-bg);
  backdrop-filter: blur(16px) saturate(140%);
  -webkit-backdrop-filter: blur(16px) saturate(140%);
  color: var(--vw-text-bright);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background var(--vw-dur-snap) var(--vw-ease),
    color var(--vw-dur-snap) var(--vw-ease),
    border-color var(--vw-dur-snap) var(--vw-ease);
  -webkit-tap-highlight-color: transparent;
}
.sheet-close:active {
  background: rgba(255, 255, 255, 0.18);
}
.sheet-close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--vw-accent-ring);
}
.sheet-close svg {
  width: 14px;
  height: 14px;
}

/* ============================================================
   MOBILE: full-height sheet
   ============================================================
   On phones the active chat becomes a full-height sheet over a dimmed,
   blurred storefront (the scrim) — not the desktop bottom-right column.
   The orb morphs away once tapped, the messages fill the screen, and
   the composer is pinned to the bottom edge, riding above the soft
   keyboard via `--pearl-kb-inset` (set by useMobileSheet). The bubbles
   keep their own glass material so they read cleanly against the scrim
   on any page. The host page is scroll-locked while open. */
@media (max-width: 767px) {
  /* iOS Safari auto-zooms when an input <= 16px gains focus. Pin the
   * composer at 16px on mobile to suppress that. */
  .composer,
  .composer::placeholder {
    font-size: 16px;
  }

  /* Idle: small orb anchored bottom-right, unchanged. */
  .orb-stack {
    right: 12px;
    bottom: max(12px, env(safe-area-inset-bottom));
  }

  /* Active: the column expands to a full-height sheet. `.orb-stack` is
   * position:absolute inside the fixed inset:0 host, so top/left/right
   * pin it to the viewport edges. `bottom: var(--pearl-kb-inset)` lifts
   * the bottom edge above the soft keyboard when it's open. */
  .widget-host.active .orb-stack {
    top: 0;
    left: 0;
    right: 0;
    bottom: var(--pearl-kb-inset, 0px);
    width: auto;
    gap: 0;
    align-items: stretch;
    justify-content: flex-end;
    padding: max(8px, env(safe-area-inset-top)) 12px 0;
  }

  /* In-sheet header (avatar + name + close) replaces the floating
   * desktop close pill on mobile. */
  .widget-host.active .sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 2px 8px;
    flex-shrink: 0;
  }
  .close-pill {
    display: none;
  }

  /* Messages fill the space between header and composer and scroll. */
  .widget-host.active .messages {
    max-height: none;
    flex: 1 1 auto;
    min-height: 0;
    padding: 4px 0;
  }

  /* Composer pinned at the bottom of the sheet, full width. The
   * safe-area bottom margin only applies when the keyboard is closed
   * (inset 0); once it opens the inset lifts the whole stack instead. */
  .widget-host.active .composer-pill {
    padding: 4px 4px 4px 14px;
    margin-top: 8px;
    margin-bottom: max(10px, env(safe-area-inset-bottom));
    flex-shrink: 0;
  }

  /* The orb morphs away once the sheet is open — the composer is the
   * sole input on mobile. Collapses to zero height so it doesn't reserve
   * space at the bottom of the column. */
  .widget-host.active .orb-row {
    opacity: 0;
    transform: scale(0.5) translateY(16px);
    pointer-events: none;
    height: 0;
    margin: 0;
    overflow: hidden;
    transition: opacity var(--vw-dur-base) var(--vw-ease),
      transform var(--vw-dur-base) var(--vw-ease);
  }
}
